// src/components/widgets/WidgetBuilderShadcn.jsx
import React, { useEffect, useState } from 'react';
import {
  Code,
  Plus,
  Trash2,
  Copy,
  ChevronDown,
  Save
} from 'lucide-react';

// shadcn components
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';

// Import our shadcn components
import WidgetConfigPanelShadcn from './WidgetConfigPanelShadcn';
import EmbedCodeModalShadcn from './EmbedCodeModalShadcn';
import CreateWidgetModalShadcn from './CreateWidgetModalShadcn';

import PageContainer from '../ui/layout/PageContainer/PageContainer';
import { useWidgets } from '../../hooks/useWidgets';
import { cn } from '../../utils/cn';

const WidgetBuilderShadcn = ({ className = '' }) => {
  const {
    widgets,
    widgetsLoading,
    widgetsError,
    selectedWidget,
    hasWidgets,
    loadWidgetDetails,
    clearSelectedWidget,
    deleteWidget,
    creatingWidget,
    savingWidget,
    publishingWidget,
    publishWidget
  } = useWidgets();

  // Local UI state
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [embedWidgetId, setEmbedWidgetId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Auto-select first widget on load if none selected
  useEffect(() => {
    if (!selectedWidget && widgets.length > 0 && !widgetsLoading) {
      console.log('WidgetBuilder: Auto-selecting first widget');
      loadWidgetDetails(widgets[0].id);
    }
  }, [selectedWidget, widgets, widgetsLoading]);

  // Handle widget selection from dropdown
  const handleWidgetSelect = (widgetId) => {
    loadWidgetDetails(widgetId);
  };

  // Handle new widget creation - opens modal
  const handleCreateWidget = () => {
    setShowCreateModal(true);
  };

  // Handle widget creation from modal
  const handleWidgetCreated = (createdWidget) => {
    // The new widget should automatically be selected by the store
    // No need to do anything, it's already selected
  };

  // Handle embed code display
  const handleShowEmbed = (widgetId) => {
    setEmbedWidgetId(widgetId);
    setShowEmbedModal(true);
  };

  // Handle widget deletion
  const handleDeleteWidget = () => {
    if (!selectedWidget) return;

    const confirmed = window.confirm(
      `Delete "${selectedWidget.widget_name}"? This cannot be undone.`
    );

    if (confirmed) {
      deleteWidget(selectedWidget.id);
      // After deletion, the store will clear selectedWidget
      // The useEffect above will auto-select the first remaining widget
    }
  };

  // Handle publish
  const handlePublish = () => {
    if (!selectedWidget) return;
    publishWidget(selectedWidget.id);
  };

  return (
    <PageContainer heightMode="viewport" className={className}>
      <div className="h-full flex flex-col">
        {/* Top Bar */}
        <div className="flex-shrink-0 h-16 border-b flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            {/* Widget Selector Dropdown */}
            {hasWidgets ? (
              <Select
                value={selectedWidget?.id || ''}
                onValueChange={handleWidgetSelect}
              >
                <SelectTrigger className="w-[300px]">
                  <SelectValue placeholder="Select a widget" />
                </SelectTrigger>
                <SelectContent>
                  {widgets.map((widget) => (
                    <SelectItem key={widget.id} value={widget.id}>
                      <div className="flex items-center gap-2">
                        <span>{widget.widget_name}</span>
                        <Badge variant={widget.is_active ? 'default' : 'secondary'} className="text-xs">
                          {widget.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        {widget.has_unpublished_changes && (
                          <Badge variant="outline" className="text-xs">
                            Draft
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="flex items-center gap-2">
                <Code className="w-5 h-5 text-muted-foreground" />
                <span className="text-lg font-medium text-muted-foreground">No Widgets</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={handleCreateWidget}
                disabled={creatingWidget}
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Widget
              </Button>

              {selectedWidget && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeleteWidget}
                    className="flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShowEmbed(selectedWidget.id)}
                    className="flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    Embed Code
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Right Side - Save Status & Publish Button */}
          {selectedWidget && (
            <div className="flex items-center gap-4">
              {/* Save Status */}
              <div className="text-sm text-muted-foreground">
                {savingWidget ? (
                  <span className="flex items-center gap-2">
                    <div className="w-3 h-3 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
                    Saving draft...
                  </span>
                ) : selectedWidget.has_unpublished_changes ? (
                  'Draft saved'
                ) : (
                  'All changes published'
                )}
              </div>

              {/* Publish Button */}
              {selectedWidget.has_unpublished_changes && (
                <Button
                  onClick={handlePublish}
                  disabled={publishingWidget || savingWidget}
                  size="sm"
                  className="flex items-center gap-2"
                >
                  {publishingWidget ? (
                    <>
                      <div className="w-4 h-4 border-2 border-background/30 border-t-background rounded-full animate-spin" />
                      Publishing...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Publish Changes
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="flex-1 min-h-0">
          {selectedWidget ? (
            /* Configuration Panel with Integrated Preview */
            <WidgetConfigPanelShadcn
              onShowEmbed={handleShowEmbed}
            />
          ) : (
            /* Empty State */
            <div className="h-full flex items-center justify-center">
              <div className="text-center max-w-md">
                <Code className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
                <h2 className="text-2xl font-semibold mb-3">
                  Create your first widget
                </h2>
                <p className="text-muted-foreground mb-6">
                  Start by creating a booking widget to accept appointments on your website.
                </p>
                <Button
                  onClick={handleCreateWidget}
                  disabled={creatingWidget}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Create Widget
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Widget Modal */}
      <CreateWidgetModalShadcn
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onWidgetCreated={handleWidgetCreated}
      />

      {/* Embed Code Modal */}
      <EmbedCodeModalShadcn
        widgetId={embedWidgetId}
        onClose={() => {
          setShowEmbedModal(false);
          setEmbedWidgetId(null);
        }}
      />
    </PageContainer>
  );
};

export default WidgetBuilderShadcn;
